import React, { Suspense, useEffect } from "react";
import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { api } from "./lib/api";
import { MobileBottomNav } from "@/components/MobileBottomNav";

function lazyRetry(factory: () => Promise<any>) {
  return React.lazy(() =>
    factory().catch((err: any) => {
      const reloaded = sessionStorage.getItem("chunk_reload");
      if (!reloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
        return new Promise(() => {});
      }
      sessionStorage.removeItem("chunk_reload");
      throw err;
    })
  );
}

const NotFound = lazyRetry(() => import("@/pages/not-found"));
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const SignDocument = lazyRetry(() => import("@/pages/SignDocument"));
const Certificate = lazyRetry(() => import("@/pages/Certificate"));
const Auth = lazyRetry(() => import("@/pages/Auth"));
const Documents = lazyRetry(() => import("@/pages/Documents"));
const ListingAgreements = lazyRetry(() => import("@/pages/ListingAgreements"));
const Templates = lazyRetry(() => import("@/pages/Templates"));
const EmbedSign = lazyRetry(() => import("@/pages/EmbedSign"));
const DealForms = lazyRetry(() => import("@/pages/DealForms"));
const DealFormSign = lazyRetry(() => import("@/pages/DealFormSign"));
const TokenSigning = lazyRetry(() => import("@/pages/TokenSigning"));
const Clients = lazyRetry(() => import("@/pages/Clients"));
const EmailTemplates = lazyRetry(() => import("@/pages/EmailTemplates"));
const TermsOfService = lazyRetry(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazyRetry(() => import("@/pages/PrivacyPolicy"));
const HipaaCompliance = lazyRetry(() => import("@/pages/HipaaCompliance"));
const Workflows = lazyRetry(() => import("@/pages/Workflows"));
const Analytics = lazyRetry(() => import("@/pages/Analytics"));
const AITemplateGenerator = lazyRetry(() => import("@/pages/AITemplateGenerator"));
const NewDocument = lazyRetry(() => import("@/pages/NewDocument"));
const NewTemplate = lazyRetry(() => import("@/pages/NewTemplate"));
const WebForms = lazyRetry(() => import("@/pages/WebForms"));
const WebFormBuilder = lazyRetry(() => import("@/pages/WebFormBuilder"));
const WebFormPublic = lazyRetry(() => import("@/pages/WebFormPublic"));
const WebFormPrint = lazyRetry(() => import("@/pages/WebFormPrint"));
const Tracking = lazyRetry(() => import("@/pages/Tracking"));
const Settings = lazyRetry(() => import("@/pages/Settings"));
const DocumentDetail = lazyRetry(() => import("@/pages/DocumentDetail"));
const DocumentPrint = lazyRetry(() => import("@/pages/DocumentPrint"));
const Contacts = lazyRetry(() => import("@/pages/Contacts"));
const Uploads = lazyRetry(() => import("@/pages/Uploads"));
const PrepareTemplate = lazyRetry(() => import("@/pages/PrepareTemplate"));

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { data: session, isLoading } = useQuery({
    queryKey: ["/api/auth/session"],
    queryFn: api.getSession,
    staleTime: 60000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session?.authenticated) {
    return <Redirect to="/auth" />;
  }

  return <Component />;
}

function AdminRoute({ component: Component }: { component: React.ComponentType }) {
  const { data: session, isLoading } = useQuery({
    queryKey: ["/api/auth/session"],
    queryFn: api.getSession,
    staleTime: 60000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session?.authenticated) {
    return <Redirect to="/auth" />;
  }

  if (!session?.isAdmin) {
    return <Redirect to="/" />;
  }

  return <Component />;
}

function AuthRoute() {
  const { data: session, isLoading } = useQuery({
    queryKey: ["/api/auth/session"],
    queryFn: api.getSession,
    staleTime: 60000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (session?.authenticated) {
    return <Redirect to="/" />;
  }

  return <Auth />;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    if (error.message?.includes("dynamically imported module") || error.message?.includes("Failed to fetch")) {
      const reloaded = sessionStorage.getItem("chunk_reload");
      if (!reloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
        return { hasError: false, error: null };
      }
      sessionStorage.removeItem("chunk_reload");
    }
    try {
      const payload = JSON.stringify({
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/client-error', new Blob([payload], { type: 'application/json' }));
      }
    } catch (_) {}
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Caught rendering error:", error, info.componentStack);
    try {
      const payload = JSON.stringify({
        source: 'componentDidCatch',
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/client-error', new Blob([payload], { type: 'application/json' }));
      }
    } catch (_) {}
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-background p-6">
          <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center shadow-lg">
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {this.state.error?.message || "An unexpected error occurred while loading this page."}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { this.setState({ hasError: false, error: null }); }}
                className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => { window.location.href = "/"; }}
                className="px-4 py-2 bg-secondary text-foreground rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Router() {
  return (
    <Switch>
      <Route path="/auth" component={AuthRoute}/>
      <Route path="/signing/:token" component={TokenSigning}/>
      <Route path="/dealform/:id" component={DealFormSign}/>
      <Route path="/embed/:id" component={EmbedSign}/>
      <Route path="/terms" component={TermsOfService}/>
      <Route path="/privacy" component={PrivacyPolicy}/>
      <Route path="/">{() => <ProtectedRoute component={Dashboard} />}</Route>
      <Route path="/analytics">{() => <ProtectedRoute component={Analytics} />}</Route>
      <Route path="/tracking">{() => <ProtectedRoute component={Tracking} />}</Route>
      <Route path="/new-document">{() => <ProtectedRoute component={NewDocument} />}</Route>
      <Route path="/document/:id">{() => <ProtectedRoute component={DocumentDetail} />}</Route>
      <Route path="/sign/:id">{() => <ProtectedRoute component={SignDocument} />}</Route>
      <Route path="/certificate/:id">{() => <ProtectedRoute component={Certificate} />}</Route>
      <Route path="/documents">{() => <ProtectedRoute component={Documents} />}</Route>
      <Route path="/listing-agreements">{() => <ProtectedRoute component={ListingAgreements} />}</Route>
      <Route path="/templates">{() => <AdminRoute component={Templates} />}</Route>
      <Route path="/new-template">{() => <AdminRoute component={NewTemplate} />}</Route>
      <Route path="/ai-generator">{() => <AdminRoute component={AITemplateGenerator} />}</Route>
      <Route path="/dealforms">{() => <AdminRoute component={DealForms} />}</Route>
      <Route path="/clients">{() => <AdminRoute component={Clients} />}</Route>
      <Route path="/hipaa">{() => <AdminRoute component={HipaaCompliance} />}</Route>
      <Route path="/workflows">{() => <AdminRoute component={Workflows} />}</Route>
      <Route path="/email-templates">{() => <ProtectedRoute component={EmailTemplates} />}</Route>
      <Route path="/webforms">{() => <AdminRoute component={WebForms} />}</Route>
      <Route path="/webforms/:id/edit">{() => <AdminRoute component={WebFormBuilder} />}</Route>
      <Route path="/webform/:id" component={WebFormPublic}/>
      <Route path="/webform-print/:subId">{() => <ProtectedRoute component={WebFormPrint} />}</Route>
      <Route path="/document-print/:docId">{() => <ProtectedRoute component={DocumentPrint} />}</Route>
      <Route path="/prepare/:templateId">{() => <AdminRoute component={PrepareTemplate} />}</Route>
      <Route path="/contacts">{() => <ProtectedRoute component={Contacts} />}</Route>
      <Route path="/uploads">{() => <AdminRoute component={Uploads} />}</Route>
      <Route path="/settings">{() => <ProtectedRoute component={Settings} />}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();

  useEffect(() => {
    let lastToastTime = 0;
    const THROTTLE_MS = 3000;

    function showErrorToast(title: string, description: string) {
      const now = Date.now();
      if (now - lastToastTime < THROTTLE_MS) return;
      lastToastTime = now;
      toast({ title, description, variant: "destructive" });
    }

    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      const msg = event.reason?.message || String(event.reason);
      if (msg.includes("401") || msg.includes("session") || msg.includes("expired")) return;
      if (msg.includes("chunk") || msg.includes("dynamically imported")) return;
      if (msg.includes("Unable to reach") || msg.includes("check your internet")) {
        showErrorToast("Connection problem", "Unable to reach the server. Please check your internet connection.");
      } else if (msg.includes("server encountered")) {
        showErrorToast("Server error", "Something went wrong on the server. Please try again in a moment.");
      } else if (msg.includes("permission")) {
        showErrorToast("Access denied", "You don't have permission to perform this action.");
      }
    }

    function handleOnline() {
      toast({ title: "Back online", description: "Your internet connection has been restored." });
      queryClient.invalidateQueries();
    }

    function handleOffline() {
      showErrorToast("No internet", "You appear to be offline. Changes will not be saved until you reconnect.");
    }

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [toast]);

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ErrorBoundary>
          <GlobalErrorHandler>
            <Suspense fallback={<PageLoader />}>
              <Router />
              <MobileBottomNav />
            </Suspense>
          </GlobalErrorHandler>
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
